/**
 * Ereignisse für den Buchungstrichter.
 *
 * Der Anlass: 59 Angebote sind online, gebucht wurde nichts — und es gab
 * keine Möglichkeit herauszufinden, woran es liegt. Vier völlig verschiedene
 * Ursachen sehen von außen gleich aus: Es kommt niemand auf die
 * Angebotsseiten; sie kommen und brechen bei der Paketauswahl ab; sie fragen
 * an und der Vermieter antwortet nicht; er sagt zu und der Kunde zahlt nicht.
 * Jede verlangt etwas anderes.
 *
 * Die Ereignisse gehen an den Google Tag Manager, der bereits geladen wird.
 * Was daraus wohin fließt, entscheidet die GTM-Konfiguration — hier wird nur
 * gemeldet, was passiert ist.
 */

/**
 * Namen, unter denen erfahrungsgemäß personenbezogene Daten landen.
 *
 * Der Trichter braucht Zahlen, keine Personen: Angebot, Paket, Preis,
 * Gästezahl. Name, Adresse und Telefonnummer haben in einem Analysewerkzeug
 * nichts verloren, und einmal übertragen bekommt man sie dort nicht mehr
 * heraus. Deshalb werden sie hier entfernt, statt sich darauf zu verlassen,
 * dass niemand sie mitgibt.
 */
const PERSONENBEZOGEN =
  /(mail|phone|telefon|name|vorname|nachname|first|last|street|stra(ss|ß)e|adresse|address|plz|zip|ort|city|kunde|customer)/i;

/** Sieht der Wert selbst nach einer Person aus? */
function verraeterisch(wert) {
  return typeof wert === "string" && /@|^\+?\d{7,}$/.test(wert);
}

export function saeubere(daten = {}) {
  const sauber = {};
  for (const [schluessel, wert] of Object.entries(daten)) {
    if (wert === undefined || wert === null || wert === "") continue;
    if (PERSONENBEZOGEN.test(schluessel)) continue;
    if (verraeterisch(wert)) continue;
    sauber[schluessel] = wert;
  }
  return sauber;
}

/**
 * Ein Ereignis melden.
 *
 * Schlägt nie fehl: Ein Analysewerkzeug darf keine Seite kaputt machen. Ist
 * kein Browser da (serverseitiges Rendern) oder blockiert ein Adblocker den
 * Tag Manager, passiert schlicht nichts.
 */
/**
 * Felder, die unsere Ereignisse mitschicken.
 *
 * Gebraucht werden sie zum Leeren: Der dataLayer vergisst nichts. Was ein
 * Ereignis setzt, steht beim nächsten noch da — am 2026-10-06 in der
 * GTM-Vorschau gesehen, wie das Polaroid-Ereignis "angebot: kruu" trug,
 * weil vorher jemand auf einen Partner geklickt hatte. In GA4 stünde dann
 * eine Kamerawahl beim falschen Anbieter.
 *
 * Deshalb setzt jedes Ereignis alle Felder — die eigenen mit Wert, die
 * übrigen auf undefined. Der Tag Manager lässt leere Felder weg, statt
 * einen alten Wert zu senden.
 */
export const MESSFELDER = [
  "angebot",
  "buchung",
  "paket",
  "gesamt",
  "gaeste",
  "anzahlung",
  "transaction_id",
  "value",
  "currency",
  "partner",
  "click_id",
  "kamera",
  "kameras",
  "bilder",
  "stelle",
];

/** Alle Messfelder auf undefined — überschreibt, was noch herumliegt. */
export function leereFelder() {
  return Object.fromEntries(MESSFELDER.map((feld) => [feld, undefined]));
}

export function ereignis(name, daten = {}) {
  if (typeof window === "undefined") return;
  const eintrag = { event: name, ...leereFelder(), ...saeubere(daten) };
  try {
    // Eigene Spur neben dem dataLayer.
    //
    // Der Tag Manager übernimmt den dataLayer und verarbeitet die Einträge —
    // sich beim Prüfen darauf zu verlassen, was danach noch im Array steht,
    // ist unzuverlässig. Und wo ein Adblocker GTM abfängt, wäre gar nichts zu
    // sehen. Diese Liste gehört uns und macht die Ereignisse prüfbar.
    window.__fgEreignisse = window.__fgEreignisse || [];
    window.__fgEreignisse.push(eintrag);
    // Gedeckelt: Eine lange Sitzung soll keinen Speicher aufbauen. Für die
    // Prüfung reichen die letzten Ereignisse, und der dataLayer bleibt
    // ohnehin der maßgebliche Weg.
    if (window.__fgEreignisse.length > 50) window.__fgEreignisse.shift();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eintrag);
  } catch {
    /* Messung ist nie wichtiger als die Seite. */
  }
}

/**
 * Die Stationen des Fotobox-Trichters.
 *
 * Als Konstanten, damit ein Tippfehler nicht still ein Ereignis erzeugt, das
 * in keinem Bericht auftaucht — der teuerste Fehler bei Messungen, weil er
 * erst Wochen später auffällt, wenn die Zahlen fehlen.
 */
/**
 * Die Sortierung der Liste.
 *
 * Bis zum 2026-10-07 hing das an einem Klick-Auslöser im Tag Manager, der
 * auf die Kennung "search_sort_…" eines Knopfes passte. Die Sortierung ist
 * jetzt ein Auswahlfeld und hat keine solche Kennung mehr — ohne eigenes
 * Ereignis wäre sie in GA4 verschwunden. "fg_" davor, damit der schon
 * veröffentlichte GA4-Tag für ^fg_ sie mitnimmt; der Wert steht in "stelle",
 * weil der Tag genau dieses Feld weiterreicht.
 */
export const SORTIERUNG_GEAENDERT = "fg_sortierung";

export const TRICHTER = {
  ANGEBOT_GESEHEN: "fg_angebot_gesehen",
  PAKET_GEWAEHLT: "fg_paket_gewaehlt",
  ANGABEN_ERREICHT: "fg_angaben_erreicht",
  ANFRAGE_GESENDET: "fg_anfrage_gesendet",
  ANFRAGE_ABGELEHNT: "fg_anfrage_abgelehnt",
  ZAHLUNG_GESTARTET: "fg_zahlung_gestartet",
  ZAHLUNG_ABGESCHLOSSEN: "fg_zahlung_abgeschlossen",
};

/**
 * Die Stationen der Polaroid-Vermietung.
 *
 * Absprache 2026-10-06: Dieser Weg wurde bis jetzt gar nicht gemessen. Die
 * Knöpfe trugen Kennungen, die mit "clickout_" anfingen, und hingen damit
 * am Auslöser für Vermittlungen — jeder Klick auf "Kamera wählen" zählte
 * in Google Ads als Klick zu einem Partner. Mit der Umbenennung fiel das
 * weg, und damit fiel der Weg aus der Messung heraus.
 *
 * Deshalb eigene Ereignisse, aus dem Code gemeldet wie beim Fotobox-
 * Trichter: Sie hängen an keiner Kennung im Markup, die sich beim nächsten
 * Umbau ändert.
 */
export const POLAROID = {
  BANNER_GEKLICKT: "fg_polaroid_banner",
  KAMERA_GEWAEHLT: "fg_polaroid_kamera_gewaehlt",
  ZAHLUNG_GESTARTET: "fg_polaroid_zahlung_gestartet",
};

/**
 * Ereignisse an den OpenAI-Pixel.
 *
 * Eigene Funktion statt window.oaiq direkt: Der Pixel ist ein Fremdskript.
 * Ein Adblocker fängt ihn ab, ein CDN-Ausfall lässt ihn fehlen — dann ist
 * window.oaiq nicht da, und ein direkter Aufruf würde die Seite mitreißen.
 * Hier kostet das Fehlen nichts.
 *
 * Der Stub im Head sammelt Aufrufe in einer Warteschlange, bis das SDK
 * geladen ist. Wir müssen also nicht warten, nur aufrufen.
 */
function pixel(...args) {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.oaiq === "function") window.oaiq(...args);
  } catch (fehler) {
    // Ein Messfehler darf nie den Klick verhindern, um den es geht.
    console.warn("Pixel-Ereignis nicht gesendet:", fehler?.message);
  }
}

/** Der Kunde verlässt uns zum Partner — das Ende unseres Trichters. */
export function pixelKlickAusgang() {
  pixel("measure", "custom", { type: "custom" }, { custom_event_name: "clickout" });
}

/** Der Kunde steigt in unseren eigenen Ablauf ein statt hinauszuklicken. */
export function pixelCheckoutGestartet() {
  pixel("measure", "checkout_started", { type: "contents" });
}
